import React, { useState, useEffect } from 'react';
import { Save, CheckCircle, AlertCircle, Loader, ArrowLeft } from 'lucide-react';

const CreateRequestForm = () => {
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    origin_address: '',
    destination_address: '',
    move_date: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);

  // Load Google Maps Script
  useEffect(() => {
    if (!window.google && process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      document.body.appendChild(script);

      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    }
  }, []);

  // Helper function to create correct API URL
  const createApiUrl = (endpoint) => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const cleanBaseUrl = baseUrl.replace(/\/+$/, '');
    const cleanEndpoint = endpoint.replace(/^\/+/, '');
    
    if (cleanBaseUrl.endsWith('/api')) {
      const endpointWithoutApi = cleanEndpoint.replace(/^api\//, '');
      return `${cleanBaseUrl}/${endpointWithoutApi}`;
    }
    
    return `${cleanBaseUrl}/${cleanEndpoint}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const url = createApiUrl('api/public/requests');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          rooms_data: {} // Empty initially, will be filled via share link
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Erstellen der Anfrage');
      }

      const data = await response.json();
      setShareUrl(data.share_url);
      setSuccess(true);
    } catch (err) {
      console.error('Error creating request:', err);
      setError(err.message || 'Fehler beim Erstellen der Anfrage');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (success && shareUrl) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 px-4">
        <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 max-w-md w-full">
          <div className="text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Anfrage erfolgreich erstellt!</h2>
            <p className="text-gray-600 mb-6">
              Sie können jetzt Ihre Umzugsgutliste bearbeiten. Klicken Sie auf den Link unten oder kopieren Sie ihn.
            </p>
            
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-sm text-gray-600 mb-2">Ihr persönlicher Link:</div>
              <a
                href={shareUrl}
                className="text-blue-600 hover:text-blue-700 break-all text-sm"
                target="_blank"
                rel="noopener noreferrer"
              >
                {shareUrl}
              </a>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => window.location.href = shareUrl}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Jetzt bearbeiten
              </button>
              <button
                onClick={() => {
                  setSuccess(false);
                  setShareUrl(null);
                  setFormData({
                    customer_name: '',
                    customer_email: '',
                    customer_phone: '',
                    origin_address: '',
                    destination_address: '',
                    move_date: ''
                  });
                }}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Neue Anfrage
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6 sm:p-8 mb-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Neue Umzugsanfrage</h1>
            <p className="text-gray-600 mt-1">Erstellen Sie eine Anfrage für Ihren Umzug</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Customer Info */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Ihre Kontaktdaten</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Max Mustermann"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-Mail <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="customer_email"
                    value={formData.customer_email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="max@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    name="customer_phone"
                    value={formData.customer_phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+49 123 456789"
                  />
                </div>
              </div>
            </div>

            {/* Addresses */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Umzugsadressen</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Abholadresse <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="origin_address"
                    id="origin-address-input"
                    value={formData.origin_address}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Straße, PLZ Ort"
                    ref={(input) => {
                      if (input && !input.getAttribute('data-autocomplete-initialized') && window.google) {
                        const autocomplete = new window.google.maps.places.Autocomplete(input, {
                          types: ['address'],
                          componentRestrictions: { country: 'de' }
                        });
                        autocomplete.addListener('place_changed', () => {
                          const place = autocomplete.getPlace();
                          if (place.formatted_address) {
                            setFormData(prev => ({ ...prev, origin_address: place.formatted_address }));
                          }
                        });
                        input.setAttribute('data-autocomplete-initialized', 'true');
                      }
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lieferadresse <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="destination_address"
                    id="destination-address-input"
                    value={formData.destination_address}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Straße, PLZ Ort"
                    ref={(input) => {
                      if (input && !input.getAttribute('data-autocomplete-initialized') && window.google) {
                        const autocomplete = new window.google.maps.places.Autocomplete(input, {
                          types: ['address'],
                          componentRestrictions: { country: 'de' }
                        });
                        autocomplete.addListener('place_changed', () => {
                          const place = autocomplete.getPlace();
                          if (place.formatted_address) {
                            setFormData(prev => ({ ...prev, destination_address: place.formatted_address }));
                          }
                        });
                        input.setAttribute('data-autocomplete-initialized', 'true');
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Move Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Umzugsdatum (optional)
              </label>
              <input
                type="date"
                name="move_date"
                value={formData.move_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Submit */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className={`w-full px-6 py-3 rounded-lg font-medium flex items-center justify-center gap-2 ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                } text-white transition-colors`}
              >
                {loading ? (
                  <>
                    <Loader className="h-5 w-5 animate-spin" />
                    Wird erstellt...
                  </>
                ) : (
                  <>
                    <Save className="h-5 w-5" />
                    Anfrage erstellen
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateRequestForm;

